import { FC, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { DIALOGS_TABLE_NAME } from '@/entities/conversations';
import { useActivePresetRefresh, useSettings } from '@/entities/settings';
import { DialogSessionRoot } from '@/features/DialogSession';
import { useDialogsSection } from '@/pages/dialogs/model/useDialogsSection';
import { usePresetSectionSync } from '@/pages/dialogs/model/usePresetSectionSync';
import { DialogVisualizerLayout } from '@/pages/dialogs/DialogVisualizerLayout/DialogVisualizerLayout';
import { DialogsOnePageLayout } from '@/pages/dialogs/DialogOnePageLayout/DialogsOnePageLayout';
import { DialogsSettings } from '@/pages/dialogs/DialogsSettings';
import { DialogsClassicLayout } from '@/pages/dialogs/DialogClassicLayout/DialogsClassicLayout';
import { PageContainer, useMainLayout } from '@/shared/ui';
import type { DialogListSectionProps } from './dialogsListSection/DialogListSection';

type Props = {
  /** Переопределяет секцию списка диалогов — используется для бета-страницы с DataGrid */
  listSection?: FC<DialogListSectionProps>;
  /**
   * Таблица, чей закреплённый пресет держится согласованным с адресом.
   * `null` — раздел без пресетов (выборки): адрес и пресеты там не связаны.
   */
  presetTableName?: string | null;
};

export const DialogsPage: FC<Props> = ({ listSection, presetTableName = DIALOGS_TABLE_NAME }) => {
  const { id } = useParams<{ id?: string }>();
  const {
    controls: { addControls, removeControl },
    isMobile,
  } = useMainLayout();
  const { dialogsPage: dialogsSettings } = useSettings();
  const layout = dialogsSettings.layout;

  // Раздел, открытый адресом, и вид списка в нём держатся согласованно во всех раскладках —
  // в том числе там, где рядом с открытой записью списка нет.
  usePresetSectionSync(presetTableName);

  // Правка общего пресета доходит до применившего его: список показывает пресет в текущем виде,
  // пока пользователь сам этот вид не донастраивал. Подхват идёт по открытому разделу: свой
  // применённый пресет и свой вид есть у каждого из них.
  const { sectionKey } = useDialogsSection(presetTableName ?? '');
  useActivePresetRefresh(sectionKey);

  useEffect(() => {
    if (!isMobile) {
      addControls({
        element: <DialogsSettings />,
        key: 'dialgos-layout-settings',
      });
    }

    return () => {
      removeControl('dialgos-layout-settings');
    };
  }, [addControls, removeControl, isMobile]);

  return (
    <PageContainer enableScroll={false} limitedWidth={false}>
      <DialogSessionRoot dialogId={id} key={id}>
        {/*
          Рендерим только активный макет. Ранее три макета монтировались одновременно
          через React <Activity> (mode visible/hidden), но в React 19.2 переключение
          visible/hidden пере-подписывает useSyncExternalStore у вложенных таблиц
          (TableLazy) и React Query, из-за чего список диалогов оставался пустым:
          первичная загрузка зависала, данные ответа /search/query не применялись к таблице.
        */}
        {isMobile ? (
          <DialogsClassicLayout listSection={listSection} />
        ) : (
          <>
            {layout === 'one-page' && <DialogsOnePageLayout listSection={listSection} />}
            {layout === 'classic' && <DialogsClassicLayout listSection={listSection} />}
            {layout === 'visualizer' && <DialogVisualizerLayout listSection={listSection} />}
          </>
        )}
      </DialogSessionRoot>
    </PageContainer>
  );
};
